import React, { useState, useEffect } from 'react';
import { useCapabilityCitationStore } from '../store/capabilityCitations';
import { CapabilityCitation } from '../types/capabilityCitation';

interface CitationTooltipProps {
  capabilityType: string;
  children: React.ReactNode;
}

export const CitationTooltip: React.FC<CitationTooltipProps> = ({ capabilityType, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const getCitationsByType = useCapabilityCitationStore(state => state.getCitationsByType);
  
  const citations = getCitationsByType(capabilityType);
  const hasCitations = citations.length > 0;
  const currentCitation = hasCitations ? citations[currentIndex] : null;

  useEffect(() => {
    if (isVisible && hasCitations) {
      // Randomize start index on hover? Or just keep 0.
      // Let's keep 0 for consistency, but maybe cycle automatically?
      // No, auto-cycle might be annoying. Let user click to cycle?
    }
  }, [isVisible, hasCitations]);

  const handleNextCitation = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % citations.length);
  };

  if (!hasCitations) {
    return <>{children}</>;
  }

  return (
    <div 
      className="relative inline-block group"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && currentCitation && (
        <div className="absolute z-50 w-72 p-4 mt-2 text-sm bg-slate-900 border border-cyan-500/30 rounded-lg shadow-xl backdrop-blur-sm left-1/2 transform -translate-x-1/2 pointer-events-none group-hover:pointer-events-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-cyan-400">{currentCitation.capabilityName}</span>
            <div className="flex items-center gap-1">
              {currentCitation.verified && (
                <span className="text-green-400 text-xs" title="Verified Public Source">✓</span>
              )}
              {citations.length > 1 && (
                <span className="text-xs text-gray-500 ml-1">
                  {currentIndex + 1}/{citations.length}
                </span>
              )}
            </div>
          </div>
          
          <p className="text-gray-300 mb-3 text-xs leading-relaxed">
            {currentCitation.summary}
          </p>
          
          <div className="border-t border-gray-700 pt-2 mt-2">
            <div className="flex justify-between items-end">
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">SOURCE: {currentCitation.citationSource}</div>
                <a 
                  href={currentCitation.citationURL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-cyan-500 hover:text-cyan-300 text-xs flex items-center gap-1 transition-colors"
                >
                  <span className="truncate max-w-[180px]">{currentCitation.citationTitle}</span>
                  <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
              
              {citations.length > 1 && (
                <button 
                  onClick={handleNextCitation}
                  className="text-xs bg-slate-800 hover:bg-slate-700 text-gray-300 px-2 py-1 rounded border border-slate-700 transition-colors"
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
